import {
  BellIcon,
  CheckCircleIcon,
  InfoIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Text,
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  UNSTABLE_ToastQueue as AriaToastQueue,
  UNSTABLE_ToastRegion as AriaToastRegion,
} from 'react-aria-components';
import { Button } from './Button';
import { cn } from './lib/cn';

/*
 * The only file that touches React Aria's toast. It ships under `UNSTABLE_`
 * names (alpha in their docs, unchanged from 1.20 to 1.21), so everything
 * else talks to `toast` and `ToastRegion` below, and an upstream rename or
 * API change stays in here.
 */

/* Named for meaning, like `BadgeTone`. */
export type ToastTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

export type ToastAction = {
  label: string;
  onAction: () => void;
};

export type ToastOptions = {
  description?: ReactNode;
  /** A button beside the message. Its toast stays until acted on or closed, unless `timeout` says otherwise. */
  action?: ToastAction;
  /** Milliseconds before it closes on its own, raised to at least 5000; `null` keeps it until closed. */
  timeout?: number | null;
  onClose?: () => void;
};

export type ToastContent = {
  tone: ToastTone;
  title: ReactNode;
  description?: ReactNode;
  action?: ToastAction;
};

/* React Aria's accessibility guidance: anything shorter is missed by people who read slowly or zoom. */
export const TOAST_MIN_TIMEOUT = 5000;
/* react-toastify's `autoClose` default, which every app runs on today. */
export const TOAST_DEFAULT_TIMEOUT = 5000;

/* Module state, outside React, so plain functions and class helpers can raise a toast. */
const queue = new AriaToastQueue<ToastContent>({ maxVisibleToasts: 5 });

function resolveTimeout({ timeout, action }: ToastOptions): number | undefined {
  if (timeout === null) {
    return undefined;
  }

  if (timeout === undefined) {
    return action ? undefined : TOAST_DEFAULT_TIMEOUT;
  }

  return Math.max(timeout, TOAST_MIN_TIMEOUT);
}

function show(
  tone: ToastTone,
  title: ReactNode,
  options: ToastOptions = {},
): string {
  const { description, action, onClose } = options;

  return queue.add(
    { tone, title, description, action },
    { timeout: resolveTimeout(options), onClose },
  );
}

/**
 * Raises a toast from anywhere, component or not. Each call returns the
 * toast's key, for `toast.close`. Needs one `ToastRegion` mounted in the app.
 */
export const toast = {
  show: (title: ReactNode, options?: ToastOptions & { tone?: ToastTone }) =>
    show(options?.tone ?? 'neutral', title, options),
  success: (title: ReactNode, options?: ToastOptions) =>
    show('success', title, options),
  error: (title: ReactNode, options?: ToastOptions) =>
    show('danger', title, options),
  warning: (title: ReactNode, options?: ToastOptions) =>
    show('warning', title, options),
  info: (title: ReactNode, options?: ToastOptions) =>
    show('info', title, options),
  close: (key: string) => queue.close(key),
  clear: () => queue.clear(),
  /** The toasts on screen now, newest first (the order they stack in). For tests and debugging. */
  visible: () => queue.visibleToasts,
};

/* Every tone has one, so a mixed stack lines its text up. */
const ICONS: Record<ToastTone, ReactNode> = {
  neutral: <BellIcon size={18} weight="fill" />,
  success: <CheckCircleIcon size={18} weight="fill" />,
  danger: <WarningCircleIcon size={18} weight="fill" />,
  warning: <WarningIcon size={18} weight="fill" />,
  info: <InfoIcon size={18} weight="fill" />,
};

const ICON_TONES: Record<ToastTone, string> = {
  neutral: 'sk:text-fg-tertiary',
  success: 'sk:text-success',
  danger: 'sk:text-danger',
  warning: 'sk:text-warning',
  info: 'sk:text-info',
};

export type ToastRegionProps = {
  className?: string;
};

/**
 * Where toasts appear: top right, above modals. Mount one per app, beside the
 * router. F6 moves keyboard focus into it, and hovering or focusing a toast
 * pauses every timer.
 */
export function ToastRegion({ className }: ToastRegionProps) {
  return (
    <AriaToastRegion
      queue={queue}
      className={cn(
        'sk:fixed sk:top-4 sk:right-4 sk:z-4000',
        'sk:flex sk:w-90 sk:max-w-[calc(100vw-32px)] sk:flex-col sk:gap-2',
        /* F6 lands on the region itself, so it needs its own ring. */
        'sk:rounded-card sk:outline-none sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-offset-4 sk:rac-focus-visible:outline-primary',
        className,
      )}
    >
      {({ toast: queued }) => (
        <AriaToast
          toast={queued}
          className={cn(
            'sk:flex sk:items-start sk:gap-2.5 sk:rounded-card sk:p-3',
            'sk:bg-surface-raised sk:inset-shadow-hairline sk:shadow-card',
            'sk:font-sans sk:outline-none',
            'sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-offset-1 sk:rac-focus-visible:outline-primary',
          )}
        >
          <span
            aria-hidden
            className={cn(
              'sk:mt-px sk:flex sk:shrink-0',
              ICON_TONES[queued.content.tone],
            )}
          >
            {ICONS[queued.content.tone]}
          </span>

          <AriaToastContent className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
            <Text
              slot="title"
              className="sk:text-sm sk:font-medium sk:text-fg sk:break-words"
            >
              {queued.content.title}
            </Text>
            {queued.content.description ? (
              <Text
                slot="description"
                className="sk:text-dense sk:tracking-dense sk:text-fg-secondary sk:break-words"
              >
                {queued.content.description}
              </Text>
            ) : null}
          </AriaToastContent>

          {queued.content.action ? (
            <Button
              size="sm"
              className="sk:shrink-0"
              onPress={() => {
                /* Closes even if the action throws; the error still surfaces. */
                try {
                  queued.content.action?.onAction();
                } finally {
                  queue.close(queued.key);
                }
              }}
            >
              {queued.content.action.label}
            </Button>
          ) : null}

          {/* A sibling of the content, so screen readers read the message before the button. */}
          <Button
            slot="close"
            variant="ghost"
            size="sm"
            shape="circle"
            className="sk:shrink-0"
          >
            <XIcon size={12} weight="bold" aria-hidden />
          </Button>
        </AriaToast>
      )}
    </AriaToastRegion>
  );
}
